import axiosClient from '@/api/axiosClient'
import { getCurrentUser } from '@/db/indexedDB'
import { ITransaction } from '@/types'
import { ELIGIBILITY_CONSENT_TYPE, ELIGIBILITY_ACTION } from '@/utils/constants'

export interface ICustomer {
  accessStatus: string
  id: string
  firstName: string
  middleName: string
  lastName: string
  email: string
  mobile: string
  emailStatus: string
  phoneStatus: string
  createdAt: string
  numberOfLoans: string
  isOnLendingPlatform: boolean
  [key: string]: any
}

export interface ApiResponse<T> {
  data: T[]
  pagination: {
    currentPage: number
    limit: number
    totalPages: number
    totalRecords: number
  }
}

export interface ITransactionsResponse extends ApiResponse<ITransaction> {
  // nextPageToken is returned for token-paginated customers, whose upstream reports no total and
  // so leaves pagination empty. Pass it back as pageToken to read the next page; an empty string
  // means the last page has been read.
  nextPageToken: string
  // How many backend calls produced this read. Set by fetchTransactions, which chains calls to fill
  // a batch, so a caller budgeting requests counts calls rather than reads.
  requests?: number
}

// Where a read starts: an opaque token for customers whose upstream pages by cursor, or a page
// number for those whose upstream pages by offset.
export interface TransactionsCursor {
  page: number
  pageToken?: string
}

// Where the read after this one starts, or undefined once there is nothing left, for customers
// paged by token and by page number alike.
export const nextTransactionsCursor = (
  read: ITransactionsResponse,
  readsSoFar: number
): TransactionsCursor | undefined => {
  if (read.nextPageToken) {
    return { page: 1, pageToken: read.nextPageToken }
  }

  const totalPages = Number(read.pagination?.totalPages ?? 0)
  const nextPage = readsSoFar + 1
  return nextPage <= totalPages ? { page: nextPage } : undefined
}

// The UI shows a page of this size; a fetch asks for several pages' worth at once so paging within
// a batch costs no round trip, and so a description search has more than one screen to look at.
export const TRANSACTIONS_PAGE_SIZE = 20
const DEFAULT_TRANSACTIONS_FETCH_LIMIT = 150
// The most a single read may ask for, matching the cap the backend applies to a cursor-paged read
// (bankSvcMaxLimit). Asking for more is quietly reduced there, so clamping here keeps what is sent
// and what is served the same thing.
export const MAX_TRANSACTIONS_FETCH_LIMIT = 500

// Reads VITE_TRANSACTIONS_FETCH_LIMIT so a lower environment can work against a smaller read -- 25
// records, say, to page through without seeding a large dataset. Anything unset, non-numeric or out
// of range falls back to the default rather than sending the backend a limit it will reject.
export const resolveFetchLimit = (configured: string | undefined): number => {
  const limit = Number(configured)
  if (!Number.isInteger(limit) || limit <= 0) {
    return DEFAULT_TRANSACTIONS_FETCH_LIMIT
  }
  return Math.min(limit, MAX_TRANSACTIONS_FETCH_LIMIT)
}

export const TRANSACTIONS_FETCH_LIMIT = resolveFetchLimit(
  import.meta.env.VITE_TRANSACTIONS_FETCH_LIMIT
)
// A token-paged batch covers a single account, so a short one is routine rather than a sign that
// the data ran out. Chaining is capped so a run of sparse accounts cannot turn one page into an
// unbounded run of calls; the cap only bites when accounts hold far less than a batch each.
const MAX_CHAINED_FETCHES = 10

export const CustomerService = {
  getAll: async (params: {
    search: string
    page: number
    pageSize: number
    sortBy?: string | undefined
    sortOrder?: 'asc' | 'desc' | undefined
  }): Promise<ApiResponse<ICustomer>> => {
    const { search, page, pageSize, sortBy, sortOrder } = params

    const requestParams: Record<string, any> = {
      search,
      page,
      limit: pageSize,
    }

    if (sortBy) {
      requestParams.sortBy = sortBy
      requestParams.isDescending = sortOrder === 'desc'
    }

    const response = await axiosClient.get('/users', {
      params: requestParams,
    })

    return response.data
  },

  updateAccessStatus: async (id: string | undefined, action: string) => {
    const response = await axiosClient.patch(`/users/${id}/manage-access`, {
      action,
    })
    return response.data
  },

  // signal aborts the read -- every chained call in it -- when the panel stops waiting on it.
  fetchTransactions: async (
    params: {
      page: number
      pageToken?: string
    },
    signal?: AbortSignal
  ): Promise<ITransactionsResponse> => {
    const { page, pageToken } = params
    const user = await getCurrentUser()

    const fetchBatch = async (token?: string): Promise<ITransactionsResponse> => {
      const response = await axiosClient.get(`/users/${user.id}/transactions`, {
        params: {
          page,
          limit: TRANSACTIONS_FETCH_LIMIT,
          ...(token ? { pageToken: token } : {}),
        },
        signal,
      })
      return response.data
    }

    const batch = await fetchBatch(pageToken)
    const data = [...batch.data]
    let nextPageToken = batch.nextPageToken
    let requests = 1

    // One token-paged call returns a single account's transactions, which is often far short of a
    // batch. Keep reading eagerly until the batch is full or the customer's data runs out, so the
    // pages that follow come out of memory and a description search has the whole batch to look
    // at. Page-and-limit customers come back with no token, so this never runs for them.
    for (
      let chained = 0;
      nextPageToken && data.length < TRANSACTIONS_FETCH_LIMIT && chained < MAX_CHAINED_FETCHES;
      chained++
    ) {
      const nextBatch = await fetchBatch(nextPageToken)
      data.push(...nextBatch.data)
      nextPageToken = nextBatch.nextPageToken
      requests++
    }

    return { ...batch, data, nextPageToken, requests }
  },

  fetchEligibiltyConsentDetails: async () => {
    const user = await getCurrentUser()
    const response = await axiosClient.get(
      `/users/${user.id}/consents?type=${ELIGIBILITY_CONSENT_TYPE}`
    )
    return response.data
  },

  revokeConsentAction: async () => {
    const user = await getCurrentUser()
    const response = await axiosClient.post(`/users/${user.id}/consents`, {
      action: ELIGIBILITY_ACTION,
      type: ELIGIBILITY_CONSENT_TYPE,
    })
    if (response.data.revokeConsentAction.response) {
      console.log('consent revoked')
    }
  },

  deleteCustomer: async (userId: string) => {
    const response = await axiosClient.delete(`/users/${userId}`)
    return response.data
  },
}
